import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const LOGO_URL = "https://static.readdy.ai/image/2e5ed1f19dde2fb934edeb3dd74e6746/24dfb05c0546ee27bc00ddff95f3527a.png";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/qui-sommes-nous", label: "Qui sommes-nous" },
  { to: "/impact-site-internet", label: "L'impact d'un site internet" },
  { to: "/#assistant-ia", label: "Votre assistant IA 24h/24h", anchor: true },
  { to: "/realisations", label: "Univers de création" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar({ transparent = false }: { transparent?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = !transparent || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-white/95 backdrop-blur border-b border-navy-100"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex items-center justify-between px-6 md:px-10 h-20">
        <Link to="/" className="flex items-center gap-3 cursor-pointer no-lift">
          <img
            src={LOGO_URL}
            srcSet={`${LOGO_URL} 68w`}
            sizes="68px"
            alt="ShopVitrineWeb"
            className="shrink-0 rounded-full"
            width={68}
            height={68}
            fetchPriority="high"
            decoding="async"
          />
          <div className="flex flex-col leading-none">
            <span
              className={`font-display text-xl md:text-[22px] tracking-tight ${
                solid ? "text-navy-900" : "text-white"
              }`}
            >
              Shop<span className={solid ? "text-coral-600" : "text-coral-400"}>Vitrine</span>Web
            </span>
            <span
              className={`mt-1 text-[11px] tracking-wide ${
                solid ? "text-navy-400" : "text-white/70"
              }`}
            >
              Votre présence en ligne, simplement
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) =>
            l.anchor ? (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-medium tracking-wide cursor-pointer transition-colors whitespace-nowrap ${
                  solid ? "text-navy-800 hover:text-coral-600" : "text-white/90 hover:text-coral-300"
                }`}
              >
                {l.label}
              </Link>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide cursor-pointer transition-colors whitespace-nowrap ${
                    isActive
                      ? solid
                        ? "text-coral-600"
                        : "text-coral-300"
                      : solid
                      ? "text-navy-800 hover:text-coral-600"
                      : "text-white/90 hover:text-coral-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to="/contact"
            className="ml-2 inline-flex items-center gap-2 rounded-md coral-shine bg-coral-400 hover:bg-coral-500 text-navy-900 font-semibold px-4 py-2 text-sm whitespace-nowrap cursor-pointer transition-colors"
          >
            Demander un devis
            <i className="ri-arrow-right-line"></i>
          </Link>
        </nav>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer no-lift ${
            solid ? "text-navy-900" : "text-white"
          }`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          <i className={`ri-${open ? "close" : "menu"}-line text-2xl`}></i>
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-navy-100 px-6 py-4 space-y-3">
          {links.map((l) =>
            l.anchor ? (
              <Link
                key={l.to}
                to={l.to}
                className="block text-base font-medium cursor-pointer text-navy-800"
              >
                {l.label}
              </Link>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `block text-base font-medium cursor-pointer ${
                    isActive ? "text-coral-600" : "text-navy-800"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md coral-shine bg-coral-400 text-navy-900 font-semibold px-4 py-2 text-sm whitespace-nowrap cursor-pointer"
          >
            Demander un devis
          </Link>
        </div>
      )}
    </header>
  );
}